import type { PurchaseTrendCell } from '@erp/rpc';
import type {
  ReportTable,
  ReportTableCell,
  ReportTableRow,
} from '@erp/shared-types';
import {
  formatCurrency,
  serializeMoney,
  sumMoney,
  toMoneyNumber,
} from '@erp/shared-utils';
import { MONTH_ORDER } from '@/utils/breakdown-transforms/monthOrdering';
import {
  divisionDisplayLabel,
  exactYtdFromAmounts,
  groupedPurchaseTrendLeaves,
  leafIdentity,
  supplierDisplayLabel,
  type PurchaseTrendTableLabels,
} from './purchaseTrendTables';

export interface PurchaseTrendExportLabels extends PurchaseTrendTableLabels {
  divisions: string;
  suppliers: string;
  total: string;
}

export function purchaseTrendExportBasename(input: {
  year: number;
  supplierLabels: string[];
  divisionLabels: string[];
}): string {
  const slug = (label: string) => label.replace(/\s+/g, '-');
  const filters = [
    ...input.supplierLabels.map(slug),
    ...input.divisionLabels.map(slug),
  ];
  const suffix = filters.length > 0 ? `-${filters.join('-')}` : '';
  return `purchases-vs-rebates-${input.year}${suffix}`;
}

export function purchaseTrendExportHeaders(
  labels: PurchaseTrendTableLabels,
): string[] {
  const headers: string[] = [];
  for (const month of MONTH_ORDER) {
    headers.push(`${month} ${labels.purchases}`);
    headers.push(`${month} ${labels.rebates}`);
  }
  headers.push(`${labels.ytd} ${labels.purchases}`);
  headers.push(`${labels.ytd} ${labels.rebates}`);
  return headers;
}

function emptyReportCell(): ReportTableCell {
  return { value: 0, formattedValue: '-' };
}

function moneyReportCell(amount: string): ReportTableCell {
  return {
    value: toMoneyNumber(amount),
    formattedValue: formatCurrency(amount),
  };
}

function amountCell(amount: string | null): ReportTableCell {
  if (amount === null) {
    return emptyReportCell();
  }
  return moneyReportCell(amount);
}

function monthAmount(
  group: PurchaseTrendCell[],
  month: number,
  metric: 'purchases' | 'reportedRebates',
): string | null {
  const amounts = group
    .filter((cell) => cell.month === month)
    .map((cell) => cell[metric]);
  return exactYtdFromAmounts(amounts);
}

interface LeafExportRow {
  key: string;
  divisionLabel: string;
  supplierLabel: string;
  amounts: Array<string | null>;
}

function leafExportRow(
  group: PurchaseTrendCell[],
  labels: PurchaseTrendTableLabels,
): LeafExportRow | null {
  const first = group[0];
  if (!first) {
    return null;
  }
  const amounts: Array<string | null> = [];
  for (let month = 1; month <= 12; month += 1) {
    amounts.push(monthAmount(group, month, 'purchases'));
    amounts.push(monthAmount(group, month, 'reportedRebates'));
  }
  amounts.push(exactYtdFromAmounts(group.map((cell) => cell.purchases)));
  amounts.push(exactYtdFromAmounts(group.map((cell) => cell.reportedRebates)));
  return {
    key: leafIdentity(first),
    divisionLabel: divisionDisplayLabel(first, labels.notInGently),
    supplierLabel: supplierDisplayLabel(first, labels.notInGently),
    amounts,
  };
}

function compareText(left: string, right: string): number {
  return left.localeCompare(right);
}

function sortLeaves(
  rows: LeafExportRow[],
  primary: 'division' | 'supplier',
): LeafExportRow[] {
  return [...rows].sort((left, right) => {
    if (primary === 'division') {
      const byDivision = compareText(left.divisionLabel, right.divisionLabel);
      if (byDivision !== 0) {
        return byDivision;
      }
      return compareText(left.supplierLabel, right.supplierLabel);
    }
    const bySupplier = compareText(left.supplierLabel, right.supplierLabel);
    if (bySupplier !== 0) {
      return bySupplier;
    }
    return compareText(left.divisionLabel, right.divisionLabel);
  });
}

function leafRowLabel(
  row: LeafExportRow,
  primary: 'division' | 'supplier',
): string {
  return primary === 'division'
    ? `${row.divisionLabel} · ${row.supplierLabel}`
    : `${row.supplierLabel} · ${row.divisionLabel}`;
}

function reportRowFromLeaf(
  row: LeafExportRow,
  primary: 'division' | 'supplier',
): ReportTableRow {
  return {
    id: row.key,
    label: leafRowLabel(row, primary),
    cells: row.amounts.map((amount) => amountCell(amount)),
  };
}

function totalAmountAt(rows: LeafExportRow[], index: number): string | null {
  const present: string[] = [];
  for (const row of rows) {
    const amount = row.amounts[index];
    if (amount !== null && amount !== undefined) {
      present.push(amount);
    }
  }
  if (present.length === 0) {
    return null;
  }
  return serializeMoney(sumMoney(present));
}

function totalReportRow(
  rows: LeafExportRow[],
  headerCount: number,
  totalLabel: string,
): ReportTableRow {
  return {
    id: 'total',
    label: totalLabel,
    cells: Array.from({ length: headerCount }, (_, index) =>
      amountCell(totalAmountAt(rows, index)),
    ),
  };
}

function reportTableFromLeaves(
  title: string,
  rows: LeafExportRow[],
  primary: 'division' | 'supplier',
  labels: PurchaseTrendExportLabels,
): ReportTable {
  const headers = purchaseTrendExportHeaders(labels);
  const dataRows = rows.map((row) => reportRowFromLeaf(row, primary));
  if (dataRows.length > 0) {
    dataRows.push(totalReportRow(rows, headers.length, labels.total));
  }
  return {
    title,
    columnHeaders: headers,
    rows: dataRows,
  };
}

export function purchaseTrendTablesToExport(
  cells: PurchaseTrendCell[],
  labels: PurchaseTrendExportLabels,
): Array<{
  title: string;
  table: ReportTable;
  rowLabelHeader: string;
  includeTotals: false;
}> {
  const leaves = groupedPurchaseTrendLeaves(cells)
    .map((group) => leafExportRow(group, labels))
    .filter((row): row is LeafExportRow => row !== null);
  return [
    {
      title: labels.divisions,
      table: reportTableFromLeaves(
        labels.divisions,
        sortLeaves(leaves, 'division'),
        'division',
        labels,
      ),
      rowLabelHeader: labels.divisions,
      includeTotals: false,
    },
    {
      title: labels.suppliers,
      table: reportTableFromLeaves(
        labels.suppliers,
        sortLeaves(leaves, 'supplier'),
        'supplier',
        labels,
      ),
      rowLabelHeader: labels.suppliers,
      includeTotals: false,
    },
  ];
}
